import { err, ok, type Result } from "neverthrow";
import type { ZodType } from "zod";
import z from "zod";

import { buildInvalidError, type InvalidError } from "@thalamos/common";

import {
  OrganisationIdSchema,
  type OrganisationIdUnbranded,
} from "../organisation.js";
import {
  ConfigurationScopeIdSchema,
  type ConfigurationScopeIdUnbranded,
} from "./scope.js";
import {
  defaultConfiguration,
  mayStateSetting,
  settingDeclarations,
  settingKeys,
  type Configuration,
  type PartialConfiguration,
  type SettingKey,
  type SettingValue,
} from "./settings.js";

/**
 * A value as any setting may hold it.
 *
 * The fold works over this rather than over `SettingValue<Key>` because it is one
 * loop across every key, and TypeScript cannot carry the correspondence between a
 * key and its own value type through an iteration. The correspondence is restored
 * once, where the resolved object is assembled — see `resolveConfiguration`.
 */
type AnySettingValue = boolean | number | string | string[];

export type SettingHolderUnbranded =
  | { kind: "default" }
  | { kind: "scope"; id: ConfigurationScopeIdUnbranded; name: string }
  | { kind: "organisation"; id: OrganisationIdUnbranded; name: string };

export const SettingHolderSchema = z.discriminatedUnion("kind", [
  z.object({ kind: z.literal("default") }),
  z.object({
    kind: z.literal("scope"),
    id: ConfigurationScopeIdSchema,
    name: z.string(),
  }),
  z.object({
    kind: z.literal("organisation"),
    id: OrganisationIdSchema,
    name: z.string(),
  }),
]) satisfies ZodType<SettingHolderUnbranded>;

/** Which layer of the chain a value came from. */
export type SettingHolder = z.infer<typeof SettingHolderSchema>;

/** What one layer of the chain says. It speaks about the keys it sets and no others. */
export type ConfigurationLayer = {
  holder: SettingHolder;
  settings: PartialConfiguration;
};

/**
 * A resolved value and where it came from.
 *
 * `clampedFrom` is present only where a layer stated something wider than the one
 * above it allowed, and resolution reduced it. That is not a corrupt state: a
 * value legal when it was written stops being legal the moment a layer above
 * tightens, and clamping is the direction that cannot hand a body a capability
 * its programme has withdrawn. It is carried so an administrator can be shown
 * what this layer asked for as well as what it is getting, rather than being left
 * to wonder why the box they ticked has no effect.
 */
export type ResolvedSetting<Value> = {
  value: Value;
  source: SettingHolder;
  clampedFrom?: Value;
};

/** Every setting, resolved, with the provenance of each. */
export type ResolvedConfiguration = {
  [Key in SettingKey]: ResolvedSetting<SettingValue<Key>>;
};

/** The schema a setting's stored value has to satisfy. */
export const settingSchema = (key: SettingKey): z.ZodType => {
  const declaration = settingDeclarations[key];

  switch (declaration.valueKind) {
    case "boolean":
      return z.boolean();
    case "whole_number":
      return z.number().int().min(declaration.minimum).max(declaration.maximum);
    case "choice":
      return z.enum(declaration.choices);
    case "choice_set":
      // Not `.min(1)`: choosing none of something is a legitimate position — a
      // body offering no instrument versions has none available, which is
      // different from having said nothing.
      return z.array(z.enum(declaration.choices));
  }

  // No `default` clause: the switch is exhaustive over `valueKind`, so another
  // value kind makes the end of this function reachable and stops it compiling —
  // which is the check a `default` throwing at runtime would replace with a
  // branch nothing can reach.
};

/**
 * A value for a named setting, parsed.
 *
 * The one way a value enters the system, whether it arrived in a request body or
 * came back out of a JSONB column — so a key nobody declares cannot be written
 * and then silently never read.
 */
export const parseSettingValue = <Key extends SettingKey>(
  key: Key,
  value: unknown,
): Result<SettingValue<Key>, InvalidError> => {
  const parsed = settingSchema(key).safeParse(value);

  return parsed.success
    ? // The schema is built from the same declaration `SettingValue<Key>` is
      // derived from, so what it produces is that type — a correspondence
      // `safeParse` erases to `unknown` because the schema was chosen at runtime.
      ok(parsed.data as SettingValue<Key>)
    : err(
        buildInvalidError({
          message: `Invalid value for setting ${key}`,
          issues: parsed.error.issues.map((issue) => issue.message),
        }),
      );
};

/**
 * The members a setting value names: a set names its own, a single choice names
 * itself, and a capability or a number names none.
 *
 * Exists so narrowing a set can be written without asking whether each of its two
 * values happens to be an array — a question with an answer that cannot occur and
 * so cannot be tested.
 */
export const settingValueMembers = (value: AnySettingValue): string[] =>
  typeof value === "boolean" || typeof value === "number" ? [] : [value].flat();

/**
 * Whether two setting values are the same, for the value shapes settings have.
 *
 * Exported because callers outside the fold compare values too — the
 * administration view asks whether reconciliation moved one — and two ways of
 * deciding that a set is unchanged is how the answers come to differ.
 */
export const sameSettingValue = (
  left: AnySettingValue,
  right: AnySettingValue,
): boolean =>
  Array.isArray(left) && Array.isArray(right)
    ? left.length === right.length && left.every((item) => right.includes(item))
    : left === right;

/**
 * What a layer's statement resolves to, given what the layers above it settled
 * on.
 *
 * A `free` setting takes the statement. A `narrowing` one takes only the part of
 * it the layers above already allowed: conjunction for a capability, intersection
 * for a set. The result can never be wider than `inherited`, which is the single
 * property this function exists to guarantee.
 */
const applyStatement = (
  key: SettingKey,
  inherited: AnySettingValue,
  stated: AnySettingValue,
): AnySettingValue => {
  if (settingDeclarations[key].override === "free") {
    return stated;
  }

  // Two cases rather than three, because narrowing is only declarable on the two
  // value kinds it means anything for: a capability, where it is conjunction, and
  // a set, where it is intersection. A single choice has no narrower form, so
  // `choiceSetting` does not take an override policy at all.
  //
  // Dispatched on the declaration rather than on the shapes of the two values,
  // which would ask whether one could be an array while the other was not — a
  // state that cannot arise, since both sides of a key come through
  // `parseSettingValue` against the same declaration.
  return settingDeclarations[key].valueKind === "boolean"
    ? inherited === true && stated === true
    : // Ordered by what was inherited rather than by what was stated, so the
      // resolved set reads the same however an administrator happened to tick it.
      settingValueMembers(inherited).filter((member) =>
        settingValueMembers(stated).includes(member),
      );
};

/**
 * What a statement resolves to under a value inherited from a layer that spoke,
 * as `resolveConfiguration` applies it.
 *
 * For a page working out what a change would do before it is made, from values
 * the server has already resolved, without folding the whole chain again.
 */
export const valueStatedUnder = ({
  key,
  inherited,
  stated,
}: {
  key: SettingKey;
  inherited: AnySettingValue;
  stated: AnySettingValue;
}): AnySettingValue => applyStatement(key, inherited, stated);

/**
 * The configuration a chain of layers produces, with the provenance of every
 * value.
 *
 * The defaults are prepended rather than expected from the caller, so the result
 * is total: every key has a value and a source, and no reader has to handle a
 * setting nobody has spoken about.
 *
 * Layers are applied in the order given — the outermost first — so a caller
 * orders the chain and nothing here walks a parent pointer. That is what lets a
 * layer be added or removed without this function changing.
 */
export const resolveConfiguration = (
  layers: ConfigurationLayer[],
): ResolvedConfiguration => {
  const resolved: Record<string, ResolvedSetting<AnySettingValue>> = {};

  for (const key of settingKeys) {
    let current: ResolvedSetting<AnySettingValue> = {
      value: defaultConfiguration[key],
      source: { kind: "default" },
    };

    for (const layer of layers) {
      const stated: AnySettingValue | undefined = layer.settings[key];

      // A statement no layer of its kind may make is ignored rather than
      // trusted, so a row that bypassed the write-time check confers nothing.
      if (stated === undefined || !mayStateSetting(key, layer.holder)) {
        continue;
      }

      // The outermost layer that speaks establishes the position; the ones below
      // it narrow against that. The default is where a setting starts, not a
      // ceiling — treating it as one would stop a programme offering anything the
      // product does not offer out of the box, which is most of what a programme
      // is for.
      const value =
        current.source.kind === "default"
          ? stated
          : applyStatement(key, current.value, stated);

      current = sameSettingValue(value, stated)
        ? { value, source: layer.holder }
        : { value, source: layer.holder, clampedFrom: stated };
    }

    resolved[key] = current;
  }

  // The loop above visits every key in `settingKeys`, so the object is complete —
  // a correspondence between key and value type that no amount of building an
  // object in a loop can show the compiler. Asserted once here so no caller has
  // to.
  return resolved as ResolvedConfiguration;
};

/**
 * The values the chain resolved to, exactly as it resolved them.
 *
 * Not reconciled, so a configuration whose settings contradict each other comes
 * back saying so. That is what a write is checked against: reconciling first
 * would repair the contradiction before anything could refuse it, and the
 * administrator who caused it would never be told.
 */
export const resolvedValues = (
  resolved: ResolvedConfiguration,
): Configuration => {
  const values: Record<string, AnySettingValue> = {};

  for (const key of settingKeys) {
    values[key] = resolved[key].value;
  }

  // Every key was visited above, a correspondence between key and value type
  // that building an object in a loop cannot show the compiler.
  return values as Configuration;
};

/**
 * What actually applies: the resolved values, with any relation between two
 * settings repaired.
 *
 * This is what the application reads, and it is total — a reader can act on it
 * without checking whether the settings agree. The repair is not a substitute for
 * the write-time refusal but the other half of it: a write that breaks a relation
 * is refused so somebody is told, and a body that a change *above* it broke is
 * reconciled so it still has something to render.
 */
export const effectiveConfiguration = (
  resolved: ResolvedConfiguration,
): Configuration =>
  relations.reduce(
    (configuration, relation) =>
      relation.holds(configuration)
        ? configuration
        : relation.reconcile(configuration),
    resolvedValues(resolved),
  );

/**
 * A relation between two settings that neither of them owns.
 *
 * Stated once, and used twice — which is the point of the shape. `holds` refuses
 * a write that would break the relation, so an administrator is told at the
 * moment they do it; `reconcile` repairs a configuration that broke anyway,
 * because a change high in a chain can invalidate a body further down that
 * nobody was editing. Writing the two separately is how they come to disagree
 * about what the relation is.
 *
 * This is the same pair narrowing uses — refuse the write, clamp the read — and
 * for the same reason: only one of them can protect a body from a change made
 * above it.
 */
type ConfigurationRelation = {
  describe: (configuration: Configuration) => string;
  holds: (configuration: Configuration) => boolean;
  reconcile: (configuration: Configuration) => Configuration;
};

const relations: ConfigurationRelation[] = [
  {
    describe: (configuration) =>
      `The default language (${configuration.defaultLanguage}) is not one of the languages this body offers`,
    holds: (configuration) =>
      configuration.availableLanguages.includes(configuration.defaultLanguage),
    // The first language the body offers. A body offering none keeps whatever
    // its default was: there is nothing to fall back to, and leaving the product
    // with no language to render in would be worse than an unoffered one.
    reconcile: (configuration) => {
      const fallback = configuration.availableLanguages[0];

      return fallback === undefined
        ? configuration
        : { ...configuration, defaultLanguage: fallback };
    },
  },
];

/**
 * Whether a configuration is internally consistent.
 *
 * Checked against the whole resolved chain after a write, because a relation
 * spans settings and only the result of the write can be tested against it.
 */
export const checkConfigurationInvariants = (
  configuration: Configuration,
): Result<Configuration, InvalidError> => {
  const issues = relations
    .filter((relation) => !relation.holds(configuration))
    .map((relation) => relation.describe(configuration));

  return issues.length === 0
    ? ok(configuration)
    : err(
        buildInvalidError({
          message: "Configuration is not internally consistent",
          issues,
        }),
      );
};

/**
 * The value a layer has to narrow against, or `null` where it has a free hand.
 *
 * `null` when nothing above has spoken: the setting is at its default, and a
 * default is a starting position rather than a ceiling — so the layer about to
 * speak is the one establishing what those below it may reduce.
 */
export const inheritedCeiling = <Key extends SettingKey>(
  resolved: ResolvedConfiguration,
  key: Key,
): SettingValue<Key> | null => {
  const setting = resolved[key];

  return setting.source.kind === "default" ? null : setting.value;
};

/**
 * Whether a layer may state this value, given what the layers above it allow.
 *
 * Refusing the write is the half of narrowing an administrator sees: resolution
 * clamps silently because it must, but somebody ticking a box they are not
 * allowed to tick should be told so at the moment they do it rather than finding
 * the setting has no effect.
 *
 * A `null` ceiling accepts anything the setting's own schema allows — see
 * `inheritedCeiling`.
 */
export const checkProposedSetting = <Key extends SettingKey>(
  key: Key,
  proposed: SettingValue<Key>,
  inherited: SettingValue<Key> | null,
): Result<SettingValue<Key>, InvalidError> => {
  if (settingDeclarations[key].override === "free" || inherited === null) {
    return ok(proposed);
  }

  return sameSettingValue(applyStatement(key, inherited, proposed), proposed)
    ? ok(proposed)
    : err(
        buildInvalidError({
          message: `Cannot widen ${key} beyond what this body inherits`,
          issues: [
            `Inherited: ${JSON.stringify(inherited)}`,
            `Proposed: ${JSON.stringify(proposed)}`,
          ],
        }),
      );
};
